import { FC, useEffect, useRef, useState } from 'react';
import theme from 'config/theme';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Column from 'components/Column';
import Spacing from 'components/Spacing';
import { ListDescription } from 'components/ListDescription';
import { CandidateCard } from 'components/Cards/CandidateCard';
import { getUrlParams } from 'helpers/utils';
import { getActionVerb, getSelectedCandidates } from 'helpers/donate';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsState } from 'selectors/recipients';
import { IPage } from 'store/donatePage/types';
import { IRecipient } from 'store/recipients/types';
import { ButtonContainer, Container, Label, Content } from '../DesktopLeftContainer/styled';
import { CandidateType } from './Candidates/Candidates';
import { ContributionRules } from './ContributionRules';
import { Toast } from 'components/Toast';
import { AmountDetailsContent } from './AmountDetails/Content';
import { AmountDetailsModal } from './AmountDetails/Modal';
import { OathRecurringInterval } from 'store/pay/types';

interface Props {
  page?: IPage;
  candidates: CandidateType[];
  handleCandidatesSelected: (index: number, checkValue: boolean) => void;
  recipients: IRecipient[];
  selectedDonationAmount?: number;
  overageMax?: number;
  onDonateClick: () => void;
  onUserDonationTotalChange: (value?: number) => void;
  setCandidates: React.Dispatch<React.SetStateAction<CandidateType[]>>;
  setSelectedDonationAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  showCustomSplit: boolean;
  onDafDonateClick: () => void;
  isDafEnabled: boolean;
  isRecurring: boolean;
  setIsRecurring: (isRecurring: boolean) => void;
  recurringInterval: OathRecurringInterval;
  isMonthlyEnabled: boolean;
}

export const ListSlate: FC<Props> = ({
  candidates,
  onDonateClick,
  handleCandidatesSelected,
  onUserDonationTotalChange,
  selectedDonationAmount,
  overageMax,
  setCandidates,
  setSelectedDonationAmount,
  showCustomSplit,
  page,
  onDafDonateClick,
  isDafEnabled,
  isRecurring,
  setIsRecurring,
  recurringInterval,
  isMonthlyEnabled,
}) => {
  const ref = useRef(null);
  const { urlPartner } = getUrlParams();
  const [isAmountDetailsModalOpen, setIsAmountDetailsModalOpen] = useState(false);
  const [buttonWithShadow, setButtonWithShadow] = useState(true);
  const recipients = useAppSelector(getRecipientsState);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const observer = new IntersectionObserver(([entry]) => {
        setButtonWithShadow(!entry.isIntersecting);
      });
      observer.observe(element);
    }
  }, []);

  const handleDonate = () => {
    setIsAmountDetailsModalOpen(true);
  };

  const currentYear = new Date().getFullYear();
  const isElectionYear = currentYear % 2 === 0;
  const electionCycleYearStart = isElectionYear ? currentYear - 1 : currentYear;
  const electionCycleYearEnd = isElectionYear ? currentYear : currentYear + 1;

  return (
    <Content>
      <Toast />
      <AmountDetailsModal
        candidates={candidates}
        page={page}
        isOpen={isAmountDetailsModalOpen}
        setIsOpen={setIsAmountDetailsModalOpen}
      >
        <AmountDetailsContent
          page={page}
          isDafEnabled={isDafEnabled}
          candidates={candidates}
          onDonateClick={onDonateClick}
          onUserDonationTotalChange={onUserDonationTotalChange}
          selectedDonationAmount={selectedDonationAmount}
          overageMax={overageMax}
          setCandidates={setCandidates}
          setSelectedDonationAmount={setSelectedDonationAmount}
          showCustomSplit={showCustomSplit}
          onDafDonateClick={onDafDonateClick}
          isRecurring={isRecurring}
          setIsRecurring={setIsRecurring}
          recurringInterval={recurringInterval}
          isMonthlyEnabled={isMonthlyEnabled}
          containerClassName="gray-bg"
        />
      </AmountDetailsModal>
      <ListDescription page={page} />
      <Container $backgroundColor={theme.colors.softGray}>
        {recipients.isLoading ? (
          <div className="loader-center">
            <Loader width={60} height={60} />
          </div>
        ) : recipients?.data?.recipients?.length === 0 ? (
          <Column>
            <Label>
              Unfortunately, this page isn't featuring any Recipients currently. Oath currently
              includes select candidates running in {electionCycleYearStart} or{' '}
              {electionCycleYearEnd}.
            </Label>
            <Spacing $size={16} />
          </Column>
        ) : (
          candidates.map((item, i) => (
            <CandidateCard
              key={i}
              index={i}
              item={item}
              variant="page"
              selectItem={handleCandidatesSelected}
              impactScoreVisibility={(urlPartner && page?.impact_score_visibility) || false}
              showMoreVisibility={(urlPartner && page?.show_more_visibility) || false}
            />
          ))
        )}
        <Spacing $size={16} />
        <ButtonContainer>
          <Button
            text={`${getActionVerb(page).toUpperCase()} NOW`}
            onPress={handleDonate}
            disabled={getSelectedCandidates(candidates).length === 0}
            amount={getSelectedCandidates(candidates).length}
            totalRecipients={candidates.length}
            withRecipients
            withShadow={buttonWithShadow}
            backgroundColor={theme.colors.oathBlue}
          />
        </ButtonContainer>
        <div ref={ref} />
        <Spacing $size={16} />
        <ContributionRules />
      </Container>
    </Content>
  );
};
